import { Link } from "react-router-dom";
import type { WorkflowStepRendererProps } from "../types";
import type { LanguageClaritySnapshot } from "../step-modules/language-clarity-review";

export function ClarityStepRenderer({
  state,
}: WorkflowStepRendererProps<LanguageClaritySnapshot>) {
  if (!state.runId) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">Language clarity review</p>
          <p className="mt-1 text-xs text-amber-900/80">
            {state.isLoading
              ? "Loading clarity review."
              : state.status === "running"
                ? "Checking for ambiguous or jargon-heavy phrasing."
                : state.snapshot?.summary ?? "Clarity review recorded."}
          </p>
        </div>
        <Link
          to={`/runs/${encodeURIComponent(state.runId)}`}
          className="text-xs font-medium text-amber-700 underline underline-offset-2"
        >
          View run
        </Link>
      </div>
      {(state.isLoading || state.status === "running") && (
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-800">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
          {state.isLoading ? "Loading clarity review" : "Clarity review in progress"}
        </div>
      )}
      {state.error && !state.run && (
        <p className="mt-3 text-xs text-red-700">{state.error.message}</p>
      )}
      {state.run?.status === "failed" && (
        <p className="mt-3 text-xs text-red-700">
          {state.run.error ?? "Clarity review failed."}
        </p>
      )}
      {state.snapshot && state.run?.status === "completed" && (
        <div className="mt-3 space-y-2 text-xs text-amber-950">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
              Verdict: {state.snapshot.verdict}
            </span>
          </div>
          {state.snapshot.flags.length > 0 && (
            <ul className="space-y-2">
              {state.snapshot.flags.slice(0, 3).map((flag) => (
                <li key={`${flag.phrase}:${flag.issueType}`}>
                  <p>
                    <span className="font-semibold">{flag.phrase}</span>
                    {" · "}
                    {flag.severity}
                  </p>
                  {flag.reason && <p className="text-amber-900/80">{flag.reason}</p>}
                  {flag.suggestions.length > 0 && (
                    <p><span className="font-semibold">Try:</span> {flag.suggestions.slice(0, 2).join(" · ")}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
          {state.snapshot.rewrittenText && (
            <p><span className="font-semibold">Suggested rewrite:</span> {state.snapshot.rewrittenText}</p>
          )}
          {state.snapshot.surfaceNotes.length > 0 && (
            <p><span className="font-semibold">Surface note:</span> {state.snapshot.surfaceNotes[0]}</p>
          )}
        </div>
      )}
    </div>
  );
}
